import React, { useState, useEffect } from "react";
import { useSnackbar } from "./context/SnackbarContext";
import { useNavigate } from "react-router-dom";
import {
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Add,
    Delete,
    Edit,
    FilterAlt,
    Launch,
    MapOutlined,
    MoreVert,
    SearchOff,
} from "@mui/icons-material";
import { project_api_path, block_options_url } from "./api_registry";
import {
    CreateProjectDialog,
    EditProjectDialog,
    DeleteProjectDialog,
    DISTRICT_HIERARCHY,
    ALL_STATES,
} from "./ProjectCUDDialogs";

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { state_name: "all", district_name: "all", block_name: "all" };

// ─── Row Action Menu ──────────────────────────────────────────────────────────

function RowActionMenu({ project, onEdit, onDelete }) {
    const [anchor, setAnchor] = useState(null);
    const open = Boolean(anchor);

    return (
        <>
            <Tooltip title="Actions">
                <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                >
                    <MoreVert fontSize="small" />
                </IconButton>
            </Tooltip>

            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{ paper: { elevation: 3, sx: { minWidth: 140, borderRadius: 2 } } }}
            >
                <MenuItem dense onClick={() => { onEdit(project); setAnchor(null); }}>
                    <ListItemIcon><Edit fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Edit</ListItemText>
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem dense onClick={() => { onDelete(project.id); setAnchor(null); }} sx={{ color: "error.main" }}>
                    <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 14 }}>Delete</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange, onClear }) {
    const hasActive = Object.values(filters).some((v) => v !== "all" && v !== "");

    const districts =
        filters.state_name && filters.state_name !== "all"
            ? (DISTRICT_HIERARCHY[filters.state_name] ?? [])
            : [];

    const [blocks, setBlocks] = useState([]);
    const [blocksLoading, setBlocksLoading] = useState(false);

    // Fetch blocks from API whenever district is selected
    useEffect(() => {
        const { state_name, district_name } = filters;
        if (!state_name || state_name === "all" || !district_name || district_name === "all") {
            setBlocks([]);
            return;
        }
        let cancelled = false;
        setBlocksLoading(true);
        fetch(`${block_options_url}?state_name=${encodeURIComponent(state_name)}&district_name=${encodeURIComponent(district_name)}`)
            .then((r) => r.json())
            .then((data) => { if (!cancelled) setBlocks(data ?? []); })
            .catch(() => { if (!cancelled) setBlocks([]); })
            .finally(() => { if (!cancelled) setBlocksLoading(false); });
        return () => { cancelled = true; };
    }, [filters.state_name, filters.district_name]); // eslint-disable-line

    const handleStateChange = (state) => {
        setBlocks([]);
        onChange({ state_name: state, district_name: "all", block_name: "all" });
    };

    const handleDistrictChange = (district) => {
        onChange({ ...filters, district_name: district, block_name: "all" });
    };

    return (
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <FilterAlt fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mr: 0.5 }}>
                    FILTER
                </Typography>

                {/* State */}
                <FormControl size="small" sx={{ width: 180 }}>
                    <InputLabel>State</InputLabel>
                    <Select
                        value={filters.state_name}
                        label="State"
                        onChange={(e) => handleStateChange(e.target.value)}
                    >
                        <MenuItem value="all"><em>All States</em></MenuItem>
                        {ALL_STATES.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* District — cascades from state */}
                <FormControl size="small" sx={{ width: 180 }} disabled={filters.state_name === "all"}>
                    <InputLabel>District</InputLabel>
                    <Select
                        value={filters.district_name}
                        label="District"
                        onChange={(e) => handleDistrictChange(e.target.value)}
                    >
                        <MenuItem value="all"><em>All Districts</em></MenuItem>
                        {districts.map((d) => (
                            <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Block — dropdown fetched from API once district is picked */}
                <FormControl
                    size="small"
                    sx={{ width: 180 }}
                    disabled={filters.district_name === "all" || blocksLoading}
                >
                    <InputLabel>{blocksLoading ? "Loading…" : "Block"}</InputLabel>
                    <Select
                        value={filters.block_name}
                        label={blocksLoading ? "Loading…" : "Block"}
                        onChange={(e) => onChange({ ...filters, block_name: e.target.value })}
                    >
                        <MenuItem value="all"><em>All Blocks</em></MenuItem>
                        {blocks.map((b) => (
                            <MenuItem key={b} value={b}>{b}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {hasActive && (
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<SearchOff fontSize="small" />}
                        onClick={onClear}
                        sx={{ color: "text.secondary", fontSize: 12 }}
                    >
                        Clear all
                    </Button>
                )}
            </Box>
        </Paper>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ProjectsPage({ page_state, project_state }) {
    const [projectToBeLounched, setProjectToBeLounched] = project_state;
    const [page, setPage] = page_state;

    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [projects, setProjects] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editProject, setEditProject] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const [filters, setFilters] = useState(EMPTY_FILTERS);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchProjects = async (pageNum, activeFilters) => {
        setLoading(true);
        const f = activeFilters ?? filters;
        try {
            const params = new URLSearchParams({
                state_name: f.state_name || "all",
                district_name: f.district_name || "all",
                block_name: f.block_name || "all",
                page_size: pageSize,
                page: pageNum,
            });
            const res = await fetch(`${project_api_path}/?${params}`);
            if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
            const data = await res.json();
            setProjects(data.items);
            setTotalPages(Math.ceil(data.total / pageSize));
            showSnackbar(`Loaded ${data.total} projects`, "success");
        } catch (err) {
            showSnackbar(`Error loading projects: ${err}`, "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchProjects(page); }, [page]); // eslint-disable-line

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
        fetchProjects(1, newFilters);
    };

    // ── Create ─────────────────────────────────────────────────────────────────
    const handleCreate = async (form, onSuccess) => {
        setActionLoading(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                state_name: form.state_name,
                district_name: form.district_name,
                block_name: form.block_name,
                add_default_layers: form.add_default_layers,
                ...(Object.keys(form.additional_properties ?? {}).length > 0 && {
                    additional_properties: form.additional_properties,
                }),
            };
            const res = await fetch(`${project_api_path}/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`Create failed: ${res.status}`);
            await fetchProjects(page);
            setCreateOpen(false);
            onSuccess?.();
            showSnackbar(`Project "${form.name}" created.`, "success");
        } catch (err) {
            showSnackbar(`${err}`, "error");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Edit ───────────────────────────────────────────────────────────────────
    const handleEdit = async (id, form) => {
        setActionLoading(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                state_name: form.state_name,
                district_name: form.district_name,
                block_name: form.block_name,
                ...(Object.keys(form.additional_properties ?? {}).length > 0 && {
                    additional_properties: form.additional_properties,
                }),
            };
            const res = await fetch(`${project_api_path}/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(await res.text());
            const updated = await res.json();
            setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            setEditOpen(false);
            showSnackbar(`Project "${updated.name}" updated.`, "success");
        } catch (err) {
            showSnackbar(err.message, "error");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        setActionLoading(true);
        try {
            const res = await fetch(`${project_api_path}/${id}`, { method: "DELETE" });
            if (!res.ok) throw new Error(await res.text());
            setProjects((prev) => prev.filter((p) => p.id !== id));
            setDeleteId(null);
            showSnackbar("Project deleted.", "success");
        } catch (err) {
            showSnackbar(err.message, "error");
        } finally {
            setActionLoading(false);
        }
    };

    // ── Loading spinner ────────────────────────────────────────────────────────
    if (loading)
        return (
            <Box sx={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2 }}>
                <CircularProgress color="primary" />
                <Typography variant="body2" color="text.secondary">Loading projects…</Typography>
            </Box>
        );

    const TABLE_COLS = ["#", "Project Name", "Description", "State", "District", "Block", "Launch", "Actions"];

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "grey.100" }}>

            {/* ── AppBar ── */}
            <AppBar position="static" elevation={2} sx={{
                zIndex: 100,
                background: "rgb(0, 0, 0)",
                backdropFilter: "blur(10px)",
            }}>
                <Toolbar
                    sx={{
                        justifyContent: "space-between",
                        padding: "0.01rem 2rem",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "1.4rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                background:
                                    "linear-gradient(90deg, #e6c673ff, #6bbff3ff)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                userSelect: "none",
                            }}
                        >
                            MapGeoid | Nexus
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* ── Body ── */}
            <Container maxWidth="xl" sx={{ flex: 1, display: "flex", flexDirection: "column", py: 3, gap: 2, overflow: "hidden" }}>

                {/* Title row */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="h6" fontWeight={700}>Projects</Typography>
                    <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
                        New Project
                    </Button>
                </Box>

                {/* Filter Bar */}
                <FilterBar
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={() => handleFilterChange(EMPTY_FILTERS)}
                />

                {/* Table */}
                <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflowY: "auto", borderRadius: 2 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {TABLE_COLS.map((col, i) => (
                                    <TableCell
                                        key={col}
                                        align={i >= TABLE_COLS.length - 2 ? "center" : "left"}
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            color: "primary.contrastText",
                                            bgcolor: "primary.main",
                                            borderBottom: "none",
                                            whiteSpace: "nowrap",
                                            py: 1.25,
                                            letterSpacing: 0.3,
                                        }}
                                    >
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {projects.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={TABLE_COLS.length} align="center" sx={{ py: 8 }}>
                                        <Typography color="text.disabled" variant="body2">
                                            No projects found. Create one to get started.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                projects.map((project, index) => (
                                    <TableRow key={project.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                        <TableCell sx={{ color: "text.disabled", fontSize: 12, width: 40 }}>
                                            {(page - 1) * pageSize + index + 1}
                                        </TableCell>

                                        <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                            {project.name}
                                        </TableCell>

                                        <TableCell sx={{ color: "text.secondary", fontSize: 13, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {project.description || (
                                                <Typography component="span" variant="caption" color="text.disabled" fontStyle="italic">—</Typography>
                                            )}
                                        </TableCell>

                                        {["state_name", "district_name", "block_name"].map((k) => (
                                            <TableCell key={k}>
                                                {project[k] ? (
                                                    <Chip label={project[k]} size="small" color="primary" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                        ))}

                                        <TableCell align="center">
                                            <Tooltip title="Open in Route Planner">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => {
                                                        setLoading(true);
                                                        setProjectToBeLounched(project);
                                                        setTimeout(() => { navigate("/route-planner"); setLoading(false); }, 100);
                                                    }}
                                                >
                                                    <Launch fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>

                                        <TableCell align="center">
                                            <RowActionMenu
                                                project={project}
                                                onEdit={(p) => { setEditProject(p); setEditOpen(true); }}
                                                onDelete={setDeleteId}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" shape="rounded" size="small" />
                </Box>
            </Container>

            {/* ── Footer ── */}
            <Box component="footer" sx={{
                px: 3, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: "rgba(0, 0, 0, 1)",
                backdropFilter: "blur(10px)", color: 'white'
            }}>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>Developed by MapGeoid</Typography>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>© {new Date().getFullYear()}</Typography>
            </Box>

            {/* ── Dialogs ── */}
            <CreateProjectDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={handleCreate}
                loading={actionLoading}
            />

            <EditProjectDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                project={editProject}
                onEdited={handleEdit}
                loading={actionLoading}
            />

            <DeleteProjectDialog
                open={Boolean(deleteId)}
                onClose={() => setDeleteId(null)}
                projectId={deleteId}
                onDeleted={handleDelete}
                loading={actionLoading}
            />
        </Box >
    );
}